import { CloudDoneIcon, CloudOffIcon, SyncIcon } from '../theme/icons';
import React from 'react';
import { Alert, Box, Chip, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import { CACHE_SERVICE_ENABLED, useCacheStatus } from '../hooks/useCacheStatus';

interface CacheStatusIndicatorProps {
  /**
   * App-level data reload used when the dev cache sidecar is not configured
   * (production). Typically remounts the active page so its own loaders re-run.
   */
  onRefresh?: () => void | Promise<void>;
}

export function CacheStatusIndicator({ onRefresh }: CacheStatusIndicatorProps) {
  const { status, triggerRefresh } = useCacheStatus();
  const [refreshError, setRefreshError] = React.useState('');
  const [localRefreshing, setLocalRefreshing] = React.useState(false);
  const [localLastRefresh, setLocalLastRefresh] = React.useState<Date | null>(null);

  // Sidecar mode reflects cache-service SSE state; otherwise track the last
  // header-triggered data reload locally.
  const lastRefresh = CACHE_SERVICE_ENABLED ? status.lastRefresh : localLastRefresh;
  const isRefreshing = CACHE_SERVICE_ENABLED ? status.isRefreshing : localRefreshing;
  const showDisconnected = CACHE_SERVICE_ENABLED && !status.connected;

  const lastRefreshLabel = lastRefresh
    ? `Refreshed ${formatAgo(lastRefresh)}`
    : 'Not yet refreshed';

  const providerEntries = Object.entries(status.providers);

  const handleRefresh = async () => {
    setRefreshError('');

    if (!CACHE_SERVICE_ENABLED) {
      // No sidecar (production): reload the app's own data instead.
      setLocalRefreshing(true);
      try {
        await onRefresh?.();
        setLocalLastRefresh(new Date());
      } catch {
        // Page components surface their own load errors — no toast needed here.
      } finally {
        setLocalRefreshing(false);
      }
      return;
    }

    try {
      await triggerRefresh();
    } catch (error) {
      setRefreshError(error instanceof Error ? error.message : 'Unable to start cache refresh.');
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {/* Per-provider chips */}
        {providerEntries.map(([provider, state]) => (
          <Chip
            key={provider}
            label={provider.toUpperCase()}
            size="small"
            icon={
              state === 'refreshing' ? (
                <CircularProgress size={10} sx={{ color: 'inherit' }} />
              ) : state === 'ok' ? (
                <CloudDoneIcon sx={{ fontSize: 14 }} />
              ) : (
                <CloudOffIcon sx={{ fontSize: 14 }} />
              )
            }
            color={state === 'ok' ? 'success' : state === 'error' ? 'error' : 'default'}
            variant="outlined"
            sx={{
              height: 24,
              fontSize: 10,
              fontWeight: 800,
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.10)',
              borderColor: 'rgba(255,255,255,0.24)',
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        ))}

        {/* Last refresh time — without the sidecar there is no cache connection to
            report, so only show the chip once a data reload has happened. */}
        {(CACHE_SERVICE_ENABLED || lastRefresh || isRefreshing) && (
          <Tooltip title={lastRefreshLabel}>
            <Chip
              label={isRefreshing ? 'Refreshing…' : lastRefreshLabel}
              size="small"
              icon={
                isRefreshing ? (
                  <CircularProgress size={10} sx={{ color: 'inherit' }} />
                ) : showDisconnected ? (
                  <CloudOffIcon sx={{ fontSize: 14 }} />
                ) : (
                  <CloudDoneIcon sx={{ fontSize: 14 }} />
                )
              }
              color={showDisconnected ? 'warning' : 'default'}
              variant="outlined"
              sx={{
                height: 24,
                fontSize: 10,
                fontWeight: 800,
                color: 'white',
                bgcolor: showDisconnected ? 'rgba(245,158,11,0.18)' : 'rgba(255,255,255,0.10)',
                borderColor: showDisconnected ? 'rgba(251,191,36,0.45)' : 'rgba(255,255,255,0.24)',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          </Tooltip>
        )}

        {/* Manual refresh button */}
        <Tooltip title={CACHE_SERVICE_ENABLED ? 'Trigger cache refresh now' : 'Refresh data now'}>
          <span>
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                p: 0.5,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.11)',
                border: '1px solid rgba(255,255,255,0.18)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.46)', borderColor: 'rgba(255,255,255,0.10)' },
              }}
            >
              <SyncIcon
                sx={{
                  fontSize: 16,
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Snackbar
        open={!!refreshError}
        autoHideDuration={6000}
        onClose={() => setRefreshError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setRefreshError('')} sx={{ width: '100%' }}>
          {refreshError}
        </Alert>
      </Snackbar>
    </>
  );
}

function formatAgo(date: Date): string {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}
